import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { bewebItem, catColor, dateClock, duration } from '../../components/parking/eventItems';
import useNotify from '../../hooks/useNotify';
import {
    LINK_BASIS,
    LINK_ITEM,
    RELEASE_TYPE,
    VISIT_CLS,
    VISIT_END,
    apiError,
    fetchVisit,
    gateLabel,
    imageUrl,
    saveLink,
    visitEvents,
} from '../../helpers/parkingEvents';
import { plateSx } from './DiffPlate';
import LinkDialog from './LinkDialog';
import NoteDialog from './NoteDialog';
import Reasons from './Reasons';

export const DRAWER_WIDTH = 480;

const huf = (n) => `${new Intl.NumberFormat('hu-HU').format(n || 0)} Ft`;

function Shot({ label, point, onZoom }) {
    const url = imageUrl(point);
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
                {label}
                {point ? ` · ${point.time.slice(11)}` : ''}
            </Typography>
            <Box
                sx={{
                    aspectRatio: '16 / 9',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {url ? (
                    <Box
                        component="img"
                        src={url}
                        alt={label}
                        loading="lazy"
                        onClick={() => onZoom(url)}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            bgcolor: '#000',
                            cursor: 'zoom-in',
                        }}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        nincs kép
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function Field({ label, children }) {
    return (
        <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                {label}
            </Typography>
            <Typography variant="body2" component="div">
                {children}
            </Typography>
        </Stack>
    );
}

const normPlate = (s) =>
    String(s || '')
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '');

// what a link brought to the parking, one line
function itemText(l) {
    const it = l.item || {};
    if (l.itemType === 'plate') return l.itemPlate || l.itemRef;
    if (l.itemType === 'release')
        return [
            RELEASE_TYPE[it.type] || 'Kiengedés',
            it.time && it.time.slice(5, 16),
            it.gate && gateLabel(it.gate),
            l.itemPlate && `olvasva: ${l.itemPlate}`,
        ]
            .filter(Boolean)
            .join(' · ');
    if (l.itemType === 'visit')
        return [it.plate, it.time && `be ${it.time.slice(5, 16)}`, it.gate]
            .filter(Boolean)
            .join(' · ');
    return [it.type, it.time && it.time.slice(5, 16), it.gate, l.itemPlate]
        .filter(Boolean)
        .join(' · ');
}

function LinkLine({ l, onRevoke }) {
    const revoked = l.status === 'revoked';
    const who =
        l.origin === 'system'
            ? 'rendszer'
            : [
                  l.createdBy && (l.createdBy.name || l.createdBy.email),
                  l.createdAt && l.createdAt.slice(0, 16),
              ]
                  .filter(Boolean)
                  .join(' · ');
    return (
        <Box sx={{ py: 0.75, opacity: revoked ? 0.6 : 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                    size="small"
                    variant="outlined"
                    color={l.origin === 'system' ? 'default' : 'primary'}
                    label={LINK_ITEM[l.itemType]}
                />
                <Typography
                    variant="body2"
                    sx={{ flex: 1, textDecoration: revoked ? 'line-through' : 'none' }}
                >
                    {itemText(l)}
                </Typography>
                {!revoked && l.status === 'approved' && (
                    <Button size="small" color="inherit" onClick={() => onRevoke(l)}>
                        Visszavonás
                    </Button>
                )}
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div">
                {who}
                {l.basis &&
                    l.basis.length > 0 &&
                    ` · ${l.basis.map((b) => LINK_BASIS[b] || b).join(', ')}`}
                {l.note && ` · „${l.note}”`}
            </Typography>
            {l.origin === 'system' && !revoked && <Reasons reasons={l.reasons} dense />}
            {revoked && (
                <Typography variant="caption" color="error" component="div">
                    visszavonta {l.revokedBy && (l.revokedBy.name || l.revokedBy.email)}{' '}
                    {l.revokedAt && l.revokedAt.slice(0, 16)} · {l.revokeReason}
                </Typography>
            )}
        </Box>
    );
}

/**
 * One visit: entry / exit images, summary, links (who linked what, revocable), the timeline from the entry to the end
 */
export default function VisitDrawer({
    id,
    onClose,
    version = 0,
    onChanged = () => {},
    onOpen = () => {},
}) {
    const { notifyError, notifySuccess } = useNotify();
    const [state, setState] = useState({ loading: false, visit: null, error: null });
    const [zoom, setZoom] = useState(null);
    const [variant, setVariant] = useState('');
    const [linking, setLinking] = useState(null); // plate item for the link dialog
    const [revoking, setRevoking] = useState(null); // link

    useEffect(() => {
        if (!id) return undefined;
        let stop = false;
        setState((s) => ({
            loading: true,
            visit: s.visit && s.visit.id === id ? s.visit : null,
            error: null,
        }));
        fetchVisit(id)
            .then((visit) => !stop && setState({ loading: false, visit, error: null }))
            .catch((error) => !stop && setState({ loading: false, visit: null, error }));
        return () => {
            stop = true;
        };
    }, [id, version]);

    useEffect(() => setVariant(''), [id]);

    const v = state.visit;
    const end = v && (VISIT_END[v.end] || { label: v.end, color: 'default' });
    const events = v
        ? visitEvents(v)
              .map((e) => ({ ...e, item: bewebItem(e) }))
              .filter((e) => !e.item.noise)
        : [];

    return (
        <Drawer
            anchor="right"
            variant="persistent"
            open={!!id}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: DRAWER_WIDTH },
                    top: 64,
                    height: 'calc(100% - 64px)',
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.5 }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                        variant="h5"
                        sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                    >
                        {v ? v.plate || '–' : ''}
                    </Typography>
                    {end && (
                        <Chip
                            size="small"
                            color={end.color}
                            label={`${end.label}${v && v.endLinked ? ' (kapcsolt)' : ''}`}
                        />
                    )}
                </Stack>
                <IconButton onClick={onClose} title="Bezárás">
                    <CloseIcon />
                </IconButton>
            </Stack>
            {state.loading && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            )}
            {state.error && (
                <Alert severity="error" sx={{ m: 2 }}>
                    {state.error.status === 404
                        ? 'Ez a látogatás nem található (újraépült a nap?)'
                        : state.error.message}
                </Alert>
            )}
            {v && (
                <>
                    {v.plates && v.plates.length > 0 && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 2, mt: -1, mb: 1 }}
                        >
                            más olvasatok:{' '}
                            <Box component="span" sx={plateSx}>
                                {v.plates.join(', ')}
                            </Box>
                        </Typography>
                    )}
                    {v.mergedInto && (
                        <Alert
                            severity="info"
                            sx={{ mx: 2, mb: 1 }}
                            action={
                                <Button size="small" onClick={() => onOpen(v.mergedInto)}>
                                    Megnyitás
                                </Button>
                            }
                        >
                            Ez a rekord egy másik parkoláshoz van kapcsolva (ugyanaz az autó).
                        </Alert>
                    )}
                    <Stack direction="row" spacing={1} sx={{ px: 2 }}>
                        <Shot label="Behajtás" point={v.in} onZoom={setZoom} />
                        <Shot label="Kihajtás" point={v.out} onZoom={setZoom} />
                    </Stack>
                    <Stack spacing={0.5} sx={{ px: 2, py: 1.5 }}>
                        <Field label="Behajtás">
                            {v.in.time} · {v.in.gate}
                            {v.inReads ? ` (${v.inReads}× olvasva)` : ''}
                        </Field>
                        <Field label="Vége">
                            {v.out ? `${v.out.time} · ${v.out.gate}` : v.endTime || '–'}
                            {v.durMin != null ? ` · ${duration(v.durMin * 60e3)}` : ''}
                        </Field>
                        {v.release && (
                            <Field label="Kiengedés">
                                {v.release.type === 'opening'
                                    ? 'Kézi sorompónyitás'
                                    : '1x kihajtó jegy'}{' '}
                                · {v.release.time.slice(11)} · {v.release.gate}
                                {v.release.user ? ` · ${v.release.user}` : ''}
                            </Field>
                        )}
                        {v.releaseBefore && (
                            <Field label="Kiengedés?">
                                {v.releaseBefore.time.slice(11)} · {v.releaseBefore.gate} — utána
                                mégis kihajtott, nem ez az autó volt
                            </Field>
                        )}
                        <Field label="Fizetve">
                            {huf(v.paid)} ({v.payments} fizetés)
                        </Field>
                        {v.fee && (
                            <Field label="Díj lekérdezve">
                                {huf(v.fee.fee)} · {v.fee.time.slice(11)}
                                {v.fee.status ? ` · ${v.fee.status}` : ''}
                            </Field>
                        )}
                        <Field label="Elutasítás">{v.rejections} a kijáratoknál</Field>
                        <Field label="Típus">
                            {VISIT_CLS[v.cls] || v.cls} · {v.kind || '–'}
                        </Field>
                        {v.card && <Field label="Bérletkártya">{v.card}</Field>}
                        <Field label="be.web azonosító">{v.ident}</Field>
                    </Stack>
                    <Divider />
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="subtitle2">Kapcsolatok</Typography>
                        {(v.links || []).map((l) => (
                            <LinkLine
                                key={l.id || `sys-${l.itemRef}`}
                                l={l}
                                onRevoke={setRevoking}
                            />
                        ))}
                        {!(v.links || []).length && (
                            <Typography variant="body2" color="text.secondary">
                                Nincs kapcsolat.
                            </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <TextField
                                size="small"
                                placeholder="Rendszámváltozat"
                                value={variant}
                                onChange={(e) => setVariant(e.target.value.toUpperCase())}
                                InputProps={{ sx: plateSx }}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                size="small"
                                disabled={normPlate(variant).length < 3}
                                onClick={() =>
                                    setLinking({
                                        type: 'plate',
                                        ref: normPlate(variant),
                                        plate: normPlate(variant),
                                        title: 'Rendszámváltozat',
                                        lines: ['az ügyfél / a kamera szerinti rendszám'],
                                    })
                                }
                            >
                                + Kapcsolat
                            </Button>
                        </Stack>
                    </Box>
                    <Divider />
                    <List dense sx={{ overflowY: 'auto', flex: 1 }}>
                        {events.map((e) => {
                            const url = imageUrl(e);
                            return (
                                <ListItemButton
                                    key={e.k}
                                    disabled={!url}
                                    onClick={() => url && setZoom(url)}
                                    sx={{ '&.Mui-disabled': { opacity: 1 } }}
                                >
                                    <ListItemText
                                        primary={
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        flexShrink: 0,
                                                        borderRadius:
                                                            e.item.sev === 'fail' ? 0 : '50%',
                                                        transform:
                                                            e.item.sev === 'fail'
                                                                ? 'rotate(45deg)'
                                                                : 'none',
                                                        bgcolor: catColor(e.item.cat),
                                                    }}
                                                />
                                                <span>{dateClock(e.ts)}</span>
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    color={
                                                        e.item.sev === 'fail'
                                                            ? 'error.main'
                                                            : 'text.primary'
                                                    }
                                                    sx={{
                                                        fontWeight:
                                                            e.item.sev === 'info' ? 400 : 700,
                                                    }}
                                                >
                                                    {e.item.label}
                                                </Typography>
                                            </Stack>
                                        }
                                        secondary={[
                                            e.item.gateLabel,
                                            e.amount ? huf(e.amount) : '',
                                            e.user,
                                            e.plate && e.plate !== v.plate ? e.plate : '',
                                            e.link ? 'kapcsolt' : '',
                                            url ? 'kép' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </>
            )}
            <LinkDialog
                open={!!linking}
                visit={v}
                item={linking}
                reasons={[]}
                onClose={() => setLinking(null)}
                onDone={() => {
                    setLinking(null);
                    setVariant('');
                    onChanged();
                }}
            />
            <NoteDialog
                open={!!revoking}
                title="Kapcsolat visszavonása"
                label="Miért téves a kapcsolat?"
                info="A kapcsolat megmarad a naplóban, visszavontként."
                min={3}
                action="Visszavonás"
                onClose={() => setRevoking(null)}
                onSubmit={(note) =>
                    saveLink({
                        action: 'revoke',
                        linkId: revoking.id || 0,
                        visitId: v.id,
                        note,
                    })
                        .then(() => {
                            notifySuccess('Kapcsolat visszavonva');
                            setRevoking(null);
                            onChanged();
                        })
                        .catch((e) => notifyError(apiError(e)))
                }
            />
            <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="xl">
                {zoom && (
                    <Box
                        component="img"
                        src={zoom}
                        alt=""
                        onClick={() => setZoom(null)}
                        sx={{ maxWidth: '96vw', maxHeight: '92vh', display: 'block' }}
                    />
                )}
            </Dialog>
        </Drawer>
    );
}
